import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Api, { FinanceContractClientResponse } from "../../Api";
import Section from "../../parts/Section";
import Fab from "../../parts/Fab";
import moment from "moment";
import classNames from "classnames";
import { intToBrl } from "../../utils";
import DiscreetMessage from "../../parts/DiscreetMessage";
import { Client, RentContract } from "../../types";

const FinanceContractClient = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [contracts, setContracts] = useState<
    FinanceContractClientResponse["contracts"] | null
  >(null);

  useEffect(() => {
    Api.financeContractClient(uuid!).then(({ client, contracts }) => {
      setClient(client);
      setContracts(contracts);
    });
  }, [uuid]);

  if (client === null || contracts === null) {
    return <DiscreetMessage text="Carregando ..." />;
  }

  return (
    <div>
      <div className="border-b">
        <Section
          className="p-4 cursor-pointer group"
          onClick={() => navigate("/financeiro/contratos/clientes")}
        >
          <div className="text-gray-400 text-sm font-bold">
            Visualizando contratos de
          </div>
          <div className="truncate text-xl font-bold group-hover:text-gray-300">
            {client.name}
          </div>
        </Section>
      </div>

      <Section className="p-4">
        {contracts.length === 0 && <DiscreetMessage text="Nenhum contrato" />}

        {contracts.length > 0 && (
          <div className="flex flex-col gap-4 pb-32">
            {contracts.map((contract) => (
              <Contract
                contract={contract.contract}
                totalReceipts={contract.totalReceipts}
                key={contract.contract.uuid}
              />
            ))}
          </div>
        )}
      </Section>

      <Fab href={`/financeiro/contratos/criar/${client.uuid}`} />
    </div>
  );
};

export default FinanceContractClient;

const Contract = ({
  contract,
  totalReceipts,
}: {
  contract: RentContract;
  totalReceipts: number;
}) => {
  const navigate = useNavigate();
  return (
    <div
      className="border rounded-lg shadow-sm p-4 cursor-pointer hover:bg-slate-50 flex flex-col gap-2"
      onClick={() => navigate(`/financeiro/contrato/${contract.uuid}`)}
    >
      <div className="flex items-center">
        <div className="flex-1 text-lg">
          <span className="font-extralight">Iniciado em </span>
          <span className="font-bold">
            {moment(contract.startDate).format("DD/MM/YYYY")}
          </span>
        </div>

        <div
          className={classNames(
            "text-xs py-0.5 px-2 rounded-full font-semibold",
            {
              "bg-red-100 text-red-800": contract.suspended,
              "bg-green-100 text-green-800": !contract.suspended,
            }
          )}
        >
          {contract.suspended ? "Suspenso" : "✓ Ativo"}
        </div>
      </div>

      <div className="flex gap-4">
        <Badge>
          <span className="font-bold">{intToBrl(contract.amount)}</span>
        </Badge>

        <Badge>
          <span className="font-bold">{totalReceipts}</span>
          <span className="font-light text-gray-500">
            {totalReceipts === 1 ? " nota" : " notas"}
          </span>
        </Badge>
      </div>
    </div>
  );
};

const Badge = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
    {children}
  </div>
);
